import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  Divider,
} from '@mui/material';
import {
  People,
  School,
  TrendingUp,
  TrendingDown,
  Login,
  Person,
  Computer,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { brandColors } from '../../theme';
import dashboardService from '../../services/dashboardService';
import activityService, { SystemActivity } from '../../services/activityService';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: { value: number; isPositive: boolean };
  onClick?: () => void;
}> = ({ title, value, icon, color, trend, onClick }) => (
  <Card 
    sx={{ 
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s ease-in-out',
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
      } : {},
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar
          sx={{
            backgroundColor: color,
            mr: 2,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          {onClick && (
            <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
              Click to view →
            </Typography>
          )}
        </Box>
      </Box>
      {trend && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {trend.isPositive ? (
            <TrendingUp sx={{ color: 'success.main', mr: 1, fontSize: 16 }} />
          ) : (
            <TrendingDown sx={{ color: 'error.main', mr: 1, fontSize: 16 }} />
          )}
          <Typography
            variant="caption"
            color={trend.isPositive ? 'success.main' : 'error.main'}
          >
            {trend.value}% from last month
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

const DashboardPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [stats, setStats] = useState([
    {
      title: 'Total Students',
      value: '0',
      icon: <People />,
      color: brandColors.primary,
      trend: { value: 0, isPositive: true },
      onClick: () => navigate('/students'),
    },
    {
      title: 'Active Schools',
      value: '0',
      icon: <School />,
      color: brandColors.secondary,
      trend: { value: 0, isPositive: true },
      onClick: () => navigate('/schools'),
    },
  ]);
  const [activities, setActivities] = useState<SystemActivity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);


  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const response = await dashboardService.getDashboardStats();
        const data = response.data;

        setStats([
          {
            title: 'Total Students',
            value: data.students.total.toLocaleString(),
            icon: <People />,
            color: brandColors.primary,
            trend: data.students.trend,
            onClick: () => navigate('/students'),
          },
          {
            title: 'Active Schools',
            value: data.schools.total.toLocaleString(),
            icon: <School />,
            color: brandColors.secondary,
            trend: data.schools.trend,
            onClick: () => navigate('/schools'),
          },
        ]);
            } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        // Keep default values on error
      }
    };

    const fetchActivities = async () => {
      try {
        setLoadingActivities(true);
        const response = await activityService.getRecentActivitiesWithLimit(10);
        if (response.success && response.data) {
          setActivities(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch activities:', error);
      } finally {
        setLoadingActivities(false);
      }
    };

    fetchDashboardStats();
    fetchActivities();
  }, [navigate]);



  return (
    <Box>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Welcome back, {user?.name || 'Admin'}! 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your schools and students efficiently with LiveOlympiad UMS.
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        {stats.map((stat, index) => (
          <Box key={index} sx={{ flex: '1 1 300px', minWidth: 0 }}>
            <StatCard {...stat} />
          </Box>
        ))}
      </Box>

      {/* Activity Log */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Activity Log
              </Typography>
              {loadingActivities ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading activities...
                  </Typography>
                </Box>
              ) : activities.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {activities.map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ 
                            bgcolor: activity.activityType === 'LOGIN' ? 'success.main' : 'primary.main',
                            width: 32,
                            height: 32
                          }}>
                            {activity.activityType === 'LOGIN' ? <Login sx={{ fontSize: 16 }} /> : <Person sx={{ fontSize: 16 }} />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {activity.userFullName || activity.userName}
                              </Typography>
                              <Chip 
                                label={activity.applicationDisplayName} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {activity.activityDescription} • {new Date(activity.activityTimestamp).toLocaleString()}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < activities.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No recent activities
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardPage; 